import React, { useCallback, useState } from "react";
import {
  IonButton,
  IonInput,
  IonItem,
  IonSelect,
  IonSelectOption,
  IonReorderGroup,
  IonReorder,
  IonAlert,
} from "@ionic/react";
import { ItemReorderEventDetail } from "@ionic/core";

import { useAssignments } from "../data/store/useAssignments";
import { useProfile } from "../data/store/useProfile";

import { GroupId } from "../models/Group";
import { Assignment } from "../models/Assignment";
import { uuidv4 } from "../util/sugar";

interface AssignmentRowProps {
  assignment: Assignment;
  onUpdate: (assignment: Assignment, title: string, status: Assignment["status"]) => void;
  onDelete: (assignmentId: string) => void;
}

const AssignmentRow: React.FC<AssignmentRowProps> = ({
  assignment,
  onUpdate,
  onDelete,
}) => {
  const [title, setTitle] = useState(assignment.title);
  const [isShowAlert, setIsShowAlert] = useState(false);

  return (
    <IonItem>
      <IonReorder slot="start" />
      <IonInput
        value={title}
        onIonInput={(e) => setTitle((e.target as any).value ?? "")}
        onIonBlur={() => onUpdate(assignment, title, assignment.status)}
        placeholder="Assignment name"
        aria-label="Assignment name"
      />
      <IonSelect
        value={assignment.status}
        aria-label="Assignment status"
        onIonChange={(e) => {
          const status = (e.target as any).value as Assignment["status"] | "delete";
          if (status === "delete") {
            setIsShowAlert(true);
          } else {
            status && onUpdate(assignment, title, status);
          }
        }}
      >
        <IonSelectOption value={"hidden"}>Hidden</IonSelectOption>
        <IonSelectOption value={"active"}>Active</IonSelectOption>
        <IonSelectOption value={"expired"}>Expired</IonSelectOption>
        <IonSelectOption value={"delete"}>Delete</IonSelectOption>
      </IonSelect>

      <IonAlert
        isOpen={isShowAlert}
        onDidDismiss={() => setIsShowAlert(false)}
        header="Confirm"
        message={`Do you want to delete ${assignment.title}?`}
        buttons={[
          {
            text: "Cancel",
            role: "cancel",
            cssClass: "secondary",
          },
          {
            text: "Delete",
            handler: () => {
              setIsShowAlert(false);
              onDelete(assignment.id);
            },
          },
        ]}
      />
    </IonItem>
  );
};

export const AssignmentAdmin: React.FC<{ groupId: GroupId }> = ({
  groupId,
}) => {
  const { ownerId } = useProfile();
  const { setAssignment, assignmentsInGroup, deleteAssignment } =
    useAssignments();
  const groupAssignments = assignmentsInGroup(groupId);

  const onSortAssignments = (event: CustomEvent<ItemReorderEventDetail>) => {
    let items = [...groupAssignments];
    items = event.detail.complete(items);
    const sortedItems = items.map((item, index) => ({ ...item, order: index }));
    for (const sortedAssignment of sortedItems) {
      setAssignment(sortedAssignment.ownerId, groupId, sortedAssignment);
    }
  };

  const updateAssignment = useCallback(
    (assignment: Assignment, title: string, status: Assignment["status"]) => {
      const freshAssignment: Assignment = { ...assignment, title, status };
      setAssignment(ownerId!, groupId, freshAssignment);
    },
    [groupId, ownerId, setAssignment]
  );

  const handleDelete = useCallback(
    (assignmentId: string) => {
      deleteAssignment(ownerId, groupId, assignmentId);
    },
    [deleteAssignment, groupId, ownerId]
  );

  const addNewAssignment = useCallback(() => {
    const id = uuidv4();
    const newAssignment: Assignment = {
      id,
      expires: { seconds: 2, nanoseconds: 3 },
      starts: { seconds: 23, nanoseconds: 2 },
      status: "hidden",
      title: "",
      order: groupAssignments.length + 1,
      ownerId: ownerId!,
    };
    setAssignment(ownerId!, groupId, newAssignment);
  }, [groupAssignments, groupId, ownerId, setAssignment]);

  return (
    <IonReorderGroup disabled={false} onIonItemReorder={onSortAssignments}>
      {groupAssignments &&
        groupAssignments.map((assignment) => (
          <AssignmentRow
            key={assignment.id}
            assignment={assignment}
            onUpdate={updateAssignment}
            onDelete={handleDelete}
          />
        ))}

      <IonButton expand="full" onClick={addNewAssignment}>
        Add Assignment
      </IonButton>
    </IonReorderGroup>
  );
};
